/**
 * The live totals bar.
 *
 * Pinned to the bottom of the roster and aligned to the grid's own columns, so
 * every figure sits under the day it belongs to. This is the number a boss is
 * actually rostering against, and the reason the app computes it locally rather
 * than waiting for the server: a wage total that lags a second behind the
 * shift you just moved is a wage total nobody trusts.
 *
 * It is never rendered for staff. Wage cost is not for staff eyes, and the
 * roster response omits it for them entirely.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { TrendingUp } from 'lucide-react';
import type { RosterTotals } from '../lib/api.ts';
import { DAY_SHORT, fmtDuration, fmtMoneyShort, isWeekend, type IsoDate } from '../lib/time.ts';
import { TweenDuration, TweenMoney } from '../components/Bits.tsx';

export function TotalsBar({
  weekDates,
  totals,
  shiftCount,
  openCount,
  syncWith,
}: {
  weekDates: IsoDate[];
  totals: RosterTotals;
  shiftCount: number;
  openCount: number;
  /**
   * The grid's scroll container. On a narrow screen the grid pans sideways, and
   * the totals must pan with it or every figure ends up sitting under the wrong
   * day. Mirroring beats making this bar scrollable in its own right: there is
   * one thing to drag and one source of truth for where the week is.
   */
  syncWith?: RefObject<HTMLDivElement | null>;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const busiest = Math.max(0, ...weekDates.map((d) => totals.byDate[d]?.cost ?? 0));

  useEffect(() => {
    const source = syncWith?.current;
    const bar = barRef.current;
    if (!source || !bar) return;
    const follow = () => {
      bar.scrollLeft = source.scrollLeft;
    };
    follow();
    source.addEventListener('scroll', follow, { passive: true });
    return () => source.removeEventListener('scroll', follow);
  }, [syncWith]);

  return (
    <div
      ref={barRef}
      // Panning happens on the grid; this bar follows it, so it must not take
      // a drag of its own or the two can be left disagreeing.
      className="sticky bottom-0 z-10"
      style={{
        overflowX: 'hidden',
        background: 'var(--sp-surface)',
        borderTop: '1px solid var(--sp-border-strong)',
        boxShadow: '0 -6px 18px rgb(20 16 12 / 0.06)',
      }}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'var(--sp-staff-col) repeat(7, minmax(var(--sp-day-col), 1fr))',
          minWidth: 'var(--sp-roster-min-w)',
        }}
      >
        <div className="flex flex-col justify-center px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-faint">
            <TrendingUp size={12} aria-hidden="true" />
            Week total
          </div>
          <div className="tabular text-[20px] font-extrabold leading-tight" style={{ color: 'var(--sp-accent-strong)' }}>
            <TweenMoney value={totals.totalCost} short />
          </div>
          <div className="tabular text-[11.5px] leading-tight text-muted">
            <TweenDuration minutes={totals.totalMinutes} /> across {shiftCount} {shiftCount === 1 ? 'shift' : 'shifts'}
            {openCount > 0 ? `, ${openCount} still open` : ''}
          </div>
        </div>

        {weekDates.map((d, i) => {
          const t = totals.byDate[d] ?? { minutes: 0, cost: 0 };
          const quiet = t.minutes === 0;
          // Each day against the busiest day, so the shape of the week is
          // visible without reading seven numbers.
          const share = busiest > 0 ? Math.round((t.cost / busiest) * 100) : 0;
          return (
            <div
              key={d}
              className="px-2.5 py-2.5"
              style={{
                borderLeft: '1px solid var(--sp-hairline)',
                background: isWeekend(d) ? 'var(--sp-bg-sunken)' : undefined,
              }}
              data-tip={`${DAY_SHORT[i]} total|${fmtDuration(t.minutes)} rostered, costing ${fmtMoneyShort(
                t.cost,
              )}. Open shifts count toward hours but cost nothing until somebody is assigned.`}
            >
              <div
                className="tabular text-[15px] font-extrabold leading-tight"
                style={{ color: quiet ? 'var(--sp-text-faint)' : 'var(--sp-text)' }}
              >
                {fmtMoneyShort(t.cost)}
              </div>
              <div
                className="tabular text-[11.5px] font-semibold leading-tight"
                style={{ color: quiet ? 'var(--sp-text-faint)' : 'var(--sp-text-muted)' }}
              >
                {fmtDuration(t.minutes)}
              </div>
              <div className="tot-bar" aria-hidden="true">
                <span style={{ width: `${share}%`, background: isWeekend(d) ? 'var(--sp-caution)' : undefined }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
